"use client";

import { useEffect, useRef, useState } from "react";
import { type ImageCarouselSection_Fields } from "~/__generated__/graphql";
import Container from "~/components/Container";
import CustomImage from "~/components/CustomImage";
import { cn } from "~/utils/cn";

const IMAGES_TO_SHOW = 3;

export default function ImageCarouselSection({
  images: imageSlices,
  greyBackgroundColor,
}: ImageCarouselSection_Fields) {
  imageSlices = imageSlices?.filter((image) => image?.image !== null);
  const images =
    imageSlices && imageSlices?.length > 2
      ? [...imageSlices, ...imageSlices]
      : imageSlices;
  const [imageIndex, setImageIndex] = useState(0);
  const lastPageIndex = imageSlices?.length ?? 0;

  const imageTrack = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!imageTrack?.current) return;
    const gapRem =
      window.innerWidth < 768 ? 1.25 : window.innerWidth < 1024 ? 1.5 : 2;
    //Translate over by the amount of an 'inactive slide', also take into
    //account the whitesapce between slides (handled by padding-right).
    imageTrack.current.style.transform =
      imageIndex - 1 === lastPageIndex
        ? ""
        : `translateX(calc(-${25 * imageIndex}% - ${gapRem * imageIndex}rem))`;
  }, [imageIndex]);

  return (
    <section className="overflow-hidden px-6 py-10 lg:px-24 xl:px-48">
      <div className="hidden grid-rows-3 gap-6 sm:grid-cols-3 sm:grid-rows-1 md:grid">
        {images
          ?.slice(imageIndex, imageIndex + IMAGES_TO_SHOW)
          .map((image, i) => (
            <CustomImage
              key={i}
              data={image?.image}
              className="aspect-4/3 h-full object-cover"
              sizes="(min-width: 1280px) calc(33.31vw - 143px), (min-width: 1040px) calc(33.18vw - 78px), calc(33.33vw - 32px)"
            />
          ))}
      </div>
      <Container containerClass="relative flex flex-col overflow-visible gap-4 md:hidden">
        {/* Space-reservers */}
        <div className="aspect-square w-2/3 md:w-5/12 lg:w-4/12"></div>
        <div className="absolute left-0 right-0 top-0 px-5 md:px-10">
          <div
            className="flex translate-x-0 flex-row items-start transition-transform"
            ref={imageTrack}
          >
            {/* Empty space so items are aligned like design. */}
            {/* <div className="aspect-square w-2/12 shrink-0 md:w-6/12 lg:w-7/12"></div> */}
            {imageSlices?.map((item, i) => (
              <div
                key={i}
                className={cn([
                  "box-content aspect-square shrink-0 pr-5 transition-all md:pr-6 lg:pr-8",
                  imageIndex === i
                    ? "w-2/3 md:w-5/12 lg:w-[27%]"
                    : "w-1/4 md:w-3/12 lg:w-[21%]",
                ])}
              >
                <CustomImage
                  className={cn("h-full w-full object-cover")}
                  data={item?.image}
                  sizes="(min-width: 1040px) calc(28.01vw - 22px), (min-width: 780px) calc(41.67vw - 33px), calc(66.74vw - 27px)"
                />
              </div>
            ))}
          </div>
        </div>
      </Container>
      {images && images.length > IMAGES_TO_SHOW && (
        <div className="mt-4 flex items-center md:hidden">
          <div className="w-10">
            {imageIndex + 1}/{imageSlices?.length}
          </div>
          <button
            className={cn(
              "material-icons-round mr-1",
              // imageIndex + 1 === 1 && "opacity-50",
            )}
            // disabled={imageIndex + 1 === 1}
            onClick={() =>
              setImageIndex(
                (prev) => (lastPageIndex + prev - 1) % lastPageIndex,
              )
            }
          >
            arrow_back
          </button>
          <button
            className={cn(
              "material-icons-round",
              // imageIndex + 1 === imageSlices?.length && "opacity-50",
            )}
            // disabled={imageIndex + 1 === imageSlices?.length}
            onClick={() => setImageIndex((prev) => (prev + 1) % lastPageIndex)}
          >
            arrow_forward
          </button>
        </div>
      )}
      {images && images.length > IMAGES_TO_SHOW && (
        <div className="mt-4 hidden items-center md:flex">
          <div className="w-10">
            {imageIndex + 1}/{lastPageIndex}
          </div>
          <button
            className={cn(
              "material-icons-round mr-1",
              // imageIndex + 1 === 1 && "opacity-50",
            )}
            // disabled={imageIndex + 1 === 1}
            onClick={() =>
              setImageIndex(
                (prev) => (lastPageIndex + prev - 1) % lastPageIndex,
              )
            }
          >
            arrow_back
          </button>
          <button
            className={cn(
              "material-icons-round",
              // imageIndex + 1 === lastPageIndex && "opacity-50",
            )}
            // disabled={imageIndex + 1 === lastPageIndex}
            onClick={() => setImageIndex((prev) => (prev + 1) % lastPageIndex)}
          >
            arrow_forward
          </button>
        </div>
      )}
    </section>
  );
}
